import React from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Switch,
  Divider,
  Paper,
  Grid,
} from "@mui/material";
import { useAuthContext } from "../../auth/useAuthContext";

export interface NHSEligibilityData {
  under16: boolean;
  over60: boolean;
  student: boolean;
  benefits: boolean;
  glaucomaDiabetes: boolean;
  familyHistory: boolean;
  other: boolean;
  otherDetails?: string;
  evidenceSeen: boolean;
  auditLog: Array<{
    field: string;
    value: boolean | string;
    timestamp: string;
    staffUserId: string;
  }>;
}

interface NHSEyeTestEligibilityProps {
  eligibilityData: NHSEligibilityData;
  onUpdateEligibility: (data: NHSEligibilityData) => void;
  disabled?: boolean;
}

export const NHSEyeTestEligibility: React.FC<NHSEyeTestEligibilityProps> = ({
  eligibilityData,
  onUpdateEligibility,
  disabled = false,
}) => {
  const { user } = useAuthContext();

  const handleCheckboxChange = (
    field: keyof NHSEligibilityData,
    checked: boolean
  ) => {
    if (
      field === "evidenceSeen" ||
      typeof eligibilityData[field] === "boolean"
    ) {
      const updatedData = {
        ...eligibilityData,
        [field]: checked,
        auditLog: [
          ...eligibilityData.auditLog,
          {
            field,
            value: checked,
            timestamp: new Date().toISOString(),
            staffUserId: user?.id || user?.userId || "unknown",
          },
        ],
      };
      onUpdateEligibility(updatedData);
    }
  };

  const handleOtherDetailsChange = (value: string) => {
    const updatedData = {
      ...eligibilityData,
      otherDetails: value,
      auditLog: [
        ...eligibilityData.auditLog,
        {
          field: "otherDetails",
          value,
          timestamp: new Date().toISOString(),
          staffUserId: user?.id || user?.userId || "unknown",
        },
      ],
    };
    onUpdateEligibility(updatedData);
  };

  return (
    <Paper sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        NHS Eye Test Eligibility
      </Typography>

      <FormGroup>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eligibilityData.under16}
                  onChange={(e) =>
                    handleCheckboxChange("under16", e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label="Under 16"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eligibilityData.over60}
                  onChange={(e) =>
                    handleCheckboxChange("over60", e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label="Over 60"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eligibilityData.student}
                  onChange={(e) =>
                    handleCheckboxChange("student", e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label="Student"
            />
          </Grid>

          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eligibilityData.benefits}
                  onChange={(e) =>
                    handleCheckboxChange("benefits", e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label="Benefits"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eligibilityData.glaucomaDiabetes}
                  onChange={(e) =>
                    handleCheckboxChange("glaucomaDiabetes", e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label="Glaucoma/Diabetes"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eligibilityData.familyHistory}
                  onChange={(e) =>
                    handleCheckboxChange("familyHistory", e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label="Family History"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eligibilityData.other}
                  onChange={(e) =>
                    handleCheckboxChange("other", e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label="Other"
            />
          </Grid>
        </Grid>

        {eligibilityData.other && (
          <Box sx={{ ml: 4, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Other Details"
              value={eligibilityData.otherDetails || ""}
              onChange={(e) => handleOtherDetailsChange(e.target.value)}
              placeholder="Please specify other eligibility criteria"
              disabled={disabled}
            />
          </Box>
        )}
      </FormGroup>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" fontWeight="medium">
          Evidence Seen:
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={eligibilityData.evidenceSeen}
              onChange={(e) =>
                handleCheckboxChange("evidenceSeen", e.target.checked)
              }
              color="primary"
              disabled={disabled}
            />
          }
          label={eligibilityData.evidenceSeen ? "Yes" : "No"}
        />
      </Box>
    </Paper>
  );
};

export default NHSEyeTestEligibility;
